import { DataGrid } from '@tableau/widgets-datagrid';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { Formatters, Messages } from '../../codegen/Localize';
import {
    AngularToReactDataGridBridge,
    IDataGridPropsScope,
} from '../../common/react/components/dataGrid/AngularToReactDataGridBridge';
import { DataGridCells } from '../../common/react/components/dataGrid/DataGridCells';
import { SortableHeader } from '../../common/react/components/dataGrid/SortableHeader';
import { ISliceable } from '../models/ISliceable';
import { IUserInfo } from '../models/IUserInfo';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { ModalService, ModalSize } from './ModalService';
import { SliceableFactory } from './SliceableFactory';
import { ToasterService, ToastType } from './ToasterService';

export enum DetailType { Success, Error }
export interface ISiteMembershipDetail{
    userName: string;
    siteName: string;
    message: string;
    userInfo: IUserInfo;
    siteRole: string;
    code: number;
    detailType: DetailType;
    isUserAdded?: boolean;
}

export class MultipleSiteRoleMembershipActionNotification {

    public static $inject = [
        '$rootScope',
        'ToasterService',
        'ModalService',
        'SliceableFactory',
        'AngularToReactDataGridBridge'
    ];

    constructor(private scope: ng.IScope,
                private toaster: ToasterService,
                private modal: ModalService,
                private SliceableFactory: SliceableFactory,
                private AngularToReactDataGridBridge: AngularToReactDataGridBridge) {}

    public notifySuccessSummary(successSummary: string){
        this.showDetailToast([], ToastType.Info, successSummary);
    }

    public notifyErrorSummary(errorSummary: string){
        this.showDetailToast([], ToastType.Error, errorSummary);
    }

    public notifySuccessResult(details: ISiteMembershipDetail[]){
        if(details.length > 0){
            const userCount = this.getUserCount(details);
            this.showDetailToast(details, ToastType.InfoNoFadeout, Messages.usersAddModifiedRemoved(
                {USER_COUNT: userCount, USER_COUNT_LOC: Formatters.formatCount(userCount)}));
        }
    }

    public notifyErrorResult(details: ISiteMembershipDetail[]){
        if(details.length>0){
            const userCount = this.getUserCount(details);
            this.showDetailToast(details, ToastType.Error, Messages.usersNotAddModifiedRemoved(
                {USER_COUNT: userCount, USER_COUNT_LOC: Formatters.formatCount(userCount)}));
        }
    }

    private getUserCount(details: ISiteMembershipDetail[]): number{
        const userSet = new Set<string>(Set.Identity);
        for(const detail of details){
            userSet.add(detail.userInfo.id);
        }
        return userSet.size();
    }

    private showDetailToast(details: ISiteMembershipDetail[], type: ToastType, summary: string) {
        const toastContentProps: MultipleSiteRoleMembershipNotificationToastContentProps = {
            summary: summary,
            detailsLinkText: Messages.details(),
            details: details,
            openModal: (anchor: JQuery, details: ISiteMembershipDetail[]) => this.openDetailsModalFromToast(details, anchor, this.scope),
        };

        this.toaster.pop({
            type: type,
            content: <MultipleSiteRoleMembershipNotification {...toastContentProps} />
        });
    }

    public openDetailsModalFromToast(
        errors: ISiteMembershipDetail[],
        anchor: JQuery,
        parentScope: ng.IScope) {

        const dataGridScope = parentScope.$new() as IDataGridPropsScope<ISiteMembershipDetail>;
        const sliceable = this.SliceableFactory.fromArray(errors);
        this.configureReactErrorDetailsGrid(dataGridScope, sliceable);

        this.modal.open({
            scope: dataGridScope,
            templateUrl: 'errorDetailsDialog.html',
            size: ModalSize.fitToContent,
            position: ModalService.anchoredTo(anchor)
        }).result.finally(() => dataGridScope.$destroy());
    }

    private configureReactErrorDetailsGrid(dataGridScope: IDataGridPropsScope<ISiteMembershipDetail>,
                                           sliceable: ISliceable<ISiteMembershipDetail>) {
        const cols: DataGrid.Column[] = [
            {
                name: 'userName-col',
                dataKey: 'userName',
                headerText: 'Name',
                headerFormatter: SortableHeader.Formatter(),
                cellFormatter: DataGridCells.PlainTextCell('userName'),
                sortable: false
            },
            {
                name: 'siteName-col',
                dataKey: 'siteName',
                headerText: 'Site',
                headerFormatter: SortableHeader.Formatter(),
                cellFormatter: DataGridCells.PlainTextCell('siteName'),
                sortable: false
            },
            {
                name: 'message-col',
                dataKey: 'message',
                headerText: 'Message',
                sortable: false,
                headerFormatter: SortableHeader.Formatter(),
                cellFormatter: DataGridCells.PlainTextCell('message'),
                cellStyle: {width: 'auto'}, // TODO: TFS 769624 - Use StyledCellFormatter instead of cellStyle
                resizable: false
            }
        ];

        this.AngularToReactDataGridBridge.attachReactDataGridToScope({
            $scope: dataGridScope,
            columns: cols,
            emptyChild: this.AngularToReactDataGridBridge.emptyTemplateElementFor({}),
            canShowFilterPanel: false
        });
        this.AngularToReactDataGridBridge.updateReactDataGridRowStore({
            scope: dataGridScope, sliceable: sliceable });
    }


}

export interface MultipleSiteRoleMembershipNotificationToastContentProps {
    summary: string;
    detailsLinkText: string;
    details: ISiteMembershipDetail[];
    openModal: (anchor: JQuery, details: ISiteMembershipDetail[]) => void;
}

export class MultipleSiteRoleMembershipNotification
extends React.PureComponent<MultipleSiteRoleMembershipNotificationToastContentProps, {}> {

    public render() {
        const detailsLink = (this.props.details.length > 0) ?
            <a onClick={() => {this.props.openModal($(ReactDOM.findDOMNode(this)), this.props.details);}}>{ this.props.detailsLinkText }</a> : null;

        return (
            <div>
                <span>{ this.props.summary + '\u00a0' /* nbsp before the details link */ }</span>
                { detailsLink }
            </div>
        );
    }
}

Module.instance().service('MultipleSiteRoleMembershipActionNotification', MultipleSiteRoleMembershipActionNotification);
